import type { PublicationRequestStatus } from "@led-signage/shared/management-api";
import { useMemo, useState, type ReactNode } from "react";
import { api, usingFixtures } from "./api/index.js";
import { newIdempotencyKey } from "./api/client.js";
import { describeFailure, type ActionFailure } from "./api/errors.js";
import { useResource, type Resource } from "./api/useResource.js";
import { MediaUploadDialog } from "./MediaUploadDialog.js";
import { PlaylistSection } from "./PlaylistSection.js";
import { PlaybackHistory } from "./PlaybackHistory.js";
import { ArchiveMedia, MediaPreview } from "./MediaPreview.js";
import { DeviceState } from "./DeviceState.js";
import { DisplayManagement } from "./DisplayManagement.js";
import { DeviceEnrollment } from "./DeviceEnrollment.js";
import { CurrentUserBadge, PermissionNotice, SessionProvider, useSession } from "./session.js";
import { EmergencySection } from "./EmergencySection.js";
import { ScheduleSection } from "./ScheduleSection.js";
import { AuthenticationRecoveryButton } from "./AuthenticationRecoveryButton.js";
import {
  approvalView,
  preparationView,
  auditLogView,
  byteLabel,
  displayView,
  durationLabel,
  mediaStatusLabel,
  mediaStatusTone,
  resolutionLabel,
} from "./present.js";

type IconName =
  | "arrow"
  | "check"
  | "clock"
  | "display"
  | "film"
  | "grid"
  | "history"
  | "menu"
  | "play"
  | "schedule"
  | "shield"
  | "upload"
  | "users";

function Icon({ name, size = 20 }: { name: IconName; size?: number }) {
  const paths: Record<IconName, ReactNode> = {
    arrow: <><path d="M5 12h14"/><path d="m14 7 5 5-5 5"/></>,
    check: <path d="m5 12 4 4L19 6"/>,
    clock: <><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></>,
    display: <><rect x="3" y="4" width="18" height="13" rx="2"/><path d="M8 21h8M12 17v4"/></>,
    film: <><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M7 5v14M17 5v14M3 9h4M17 9h4M3 15h4M17 15h4"/></>,
    grid: <><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></>,
    history: <><path d="M3 12a9 9 0 1 0 3-6.7L3 8"/><path d="M3 3v5h5M12 7v5l4 2"/></>,
    menu: <><path d="M4 7h16M4 12h16M4 17h16"/></>,
    play: <path d="m9 7 8 5-8 5V7Z"/>,
    schedule: <><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M16 3v4M8 3v4M3 10h18"/></>,
    shield: <><path d="M12 3 5 6v5c0 4.6 2.8 8.1 7 10 4.2-1.9 7-5.4 7-10V6l-7-3Z"/><path d="m9 12 2 2 4-5"/></>,
    upload: <><path d="M12 16V4m0 0L7 9m5-5 5 5"/><path d="M4 15v5h16v-5"/></>,
    users: <><circle cx="9" cy="8" r="3"/><path d="M3 20v-2a6 6 0 0 1 12 0v2M16 5a3 3 0 0 1 0 6M18 14a5 5 0 0 1 3 4.6V20"/></>,
  };

  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {paths[name]}
    </svg>
  );
}

function Brand({ inverse = false }: { inverse?: boolean }) {
  return (
    <a className={`brand ${inverse ? "brand--inverse" : ""}`} href="/" aria-label="LED Signage Platform トップ">
      <span className="brand__mark"><span>L</span><span>E</span><span>D</span></span>
      <span className="brand__text">SIGNAGE<br />PLATFORM</span>
    </a>
  );
}

function LandingPage() {
  return (
    <div className="landing">
      <header className="landing-header">
        <Brand />
        <nav className="landing-nav" aria-label="メインナビゲーション">
          <a href="#features">できること</a>
          <a href="#flow">導入の流れ</a>
          <a href="#safety">安全設計</a>
          <a className="pill-link" href="#contact">お問い合わせ <Icon name="arrow" size={17} /></a>
        </nav>
        <a className="menu-button" href="#contact" aria-label="お問い合わせへ移動"><Icon name="menu" size={24} /></a>
      </header>

      <main>
        <section className="hero">
          <div className="hero__copy">
            <p className="eyebrow">CLOUD × LOCAL PLAYBACK</p>
            <h1>大型LEDを、<br /><span>もっと自由に。</span></h1>
            <p className="hero__lead">動画の登録、承認、スケジュール、複数拠点への配信まで。専用機器に縛られない、安全なデジタルサイネージ運用を。</p>
            <div className="hero__actions">
              <a className="button button--dark" href="#contact">導入について相談する <Icon name="arrow" /></a>
              <a className="text-link" href="/console/">管理画面デモを見る <Icon name="arrow" size={18} /></a>
            </div>
          </div>
          <div className="hero__visual" aria-label="LED配信イメージ">
            <div className="signal signal--one" />
            <div className="signal signal--two" />
            <div className="led-wall">
              <div className="led-wall__top"><span>NAHA / MAIN LED</span><span className="live-dot">ON AIR</span></div>
              <div className="led-wall__art">
                <span className="art-kicker">SUMMER<br />COLLECTION</span>
                <span className="art-year">2026</span>
              </div>
              <div className="led-wall__footer"><span>Next 18:00</span><span>1920 × 1080</span></div>
            </div>
            <div className="floating-card floating-card--status"><span className="status-dot status-dot--online" />5台中 5台 稼働中</div>
            <div className="floating-card floating-card--schedule"><Icon name="schedule" size={18} /><span><b>次の切替</b> 18:00 キャンペーンB</span></div>
          </div>
        </section>

        <section className="ticker" aria-label="サービスの特徴">
          <div>PLAY ANYWHERE <span>●</span> STAY IN CONTROL <span>●</span> KEEP PLAYING OFFLINE <span>●</span> PLAY ANYWHERE <span>●</span></div>
        </section>

        <section className="intro section-shell" id="features">
          <div className="section-label"><span>01</span> About</div>
          <div className="intro__content">
            <p className="eyebrow eyebrow--blue">ABOUT THE PLATFORM</p>
            <h2>配信の手間を減らし、<br />現場の安心を増やす。</h2>
            <p>ブラウザから動画と予定を登録すると、各拠点の再生機が事前に取得。インターネットが切れても、予定どおりの再生を続けます。</p>
          </div>
          <div className="stat-grid">
            <article><strong>24<span>h</span></strong><p>オフラインでも<br />ローカル再生を継続</p></article>
            <article><strong>5<span>+</span></strong><p>複数のLEDを<br />個別に設定</p></article>
            <article><strong>2<span>step</span></strong><p>作成と承認を分離し<br />誤配信を防止</p></article>
          </div>
        </section>

        <section className="features section-shell">
          <div className="feature-card feature-card--blue">
            <span className="feature-card__number">01</span><Icon name="upload" size={34} />
            <h3>かんたん動画管理</h3><p>MP4動画をブラウザから登録。長さ、解像度、音声有無を一覧で確認できます。</p>
          </div>
          <div className="feature-card feature-card--green">
            <span className="feature-card__number">02</span><Icon name="schedule" size={34} />
            <h3>拠点別スケジュール</h3><p>LEDごとにプレイリストと期間を指定。前日までの予約運用をわかりやすく支えます。</p>
          </div>
          <div className="feature-card feature-card--cream">
            <span className="feature-card__number">03</span><Icon name="shield" size={34} />
            <h3>承認付きの安全配信</h3><p>作成者と承認者を分離。誰が、いつ、何を変更したかを操作履歴に残します。</p>
          </div>
        </section>

        <section className="flow" id="flow">
          <div className="section-shell">
            <div className="section-label section-label--light"><span>02</span> How it works</div>
            <div className="flow__heading"><div><p className="eyebrow">SIMPLE WORKFLOW</p><h2>公開まで、迷わない。</h2></div><p>日々の運用は4つの手順。端末への配信状況まで、ひとつの画面で追跡できます。</p></div>
            <ol className="flow-list">
              <li><span>1</span><Icon name="upload" size={28} /><div><b>アップロード</b><small>動画を登録・自動検査</small></div></li>
              <li><span>2</span><Icon name="film" size={28} /><div><b>プレイリスト</b><small>再生順と回数を設定</small></div></li>
              <li><span>3</span><Icon name="schedule" size={28} /><div><b>スケジュール</b><small>LEDと期間を選択</small></div></li>
              <li><span>4</span><Icon name="check" size={28} /><div><b>承認・公開</b><small>確認して安全に配信</small></div></li>
            </ol>
          </div>
        </section>

        <section className="safety section-shell" id="safety">
          <div className="safety__visual"><div className="shield-orbit"><Icon name="shield" size={70} /><span className="orbit orbit--1" /><span className="orbit orbit--2" /></div></div>
          <div className="safety__copy">
            <div className="section-label"><span>03</span> Safety</div>
            <p className="eyebrow eyebrow--blue">BUILT FOR RELIABILITY</p>
            <h2>止めない。<br />間違えない。<br />あとから追える。</h2>
            <ul>
              <li><Icon name="check" />回線断でも再生を継続</li>
              <li><Icon name="check" />公開前の二者承認</li>
              <li><Icon name="check" />改ざん・破損ファイルを検知</li>
              <li><Icon name="check" />すべての操作を監査ログへ記録</li>
            </ul>
          </div>
        </section>

        <section className="contact" id="contact">
          <div className="contact__intro">
            <p className="eyebrow">CONTACT</p><h2>大型LEDの運用、<br />一緒に整理しませんか。</h2>
            <p>機器構成や現在の運用が決まっていなくても大丈夫です。設置台数、動画の種類、お困りごとをお聞かせください。</p>
            <div className="contact__note"><Icon name="clock" /><span>通常2営業日以内にご連絡します<br /><small>※ 現在は画面プロトタイプです</small></span></div>
          </div>
          <form className="contact-form" onSubmit={(event) => event.preventDefault()}>
            <label>会社名・団体名<input type="text" placeholder="例）株式会社サンプル" /></label>
            <div className="field-row"><label>お名前<span>必須</span><input required type="text" placeholder="山田 太郎" /></label><label>メールアドレス<span>必須</span><input required type="email" placeholder="info@example.com" /></label></div>
            <label>ご相談内容<select defaultValue=""><option value="" disabled>選択してください</option><option>新規導入について</option><option>既存システムからの移行</option><option>料金・構成について</option><option>その他</option></select></label>
            <label>お問い合わせ内容<textarea rows={5} placeholder="LEDの台数や現在のお困りごとをご記入ください" /></label>
            <label className="privacy"><input type="checkbox" />プライバシーポリシーに同意する</label>
            <button className="button button--dark" type="submit">入力内容を確認する <Icon name="arrow" /></button>
          </form>
        </section>
      </main>

      <footer className="landing-footer"><Brand inverse /><p>大型LED動画配信システム（仮称）</p><a href="/console/">管理画面デモ</a><small>© 2026 LED Signage Platform</small></footer>
    </div>
  );
}

type AdminSection = "dashboard" | "media" | "playlists" | "schedule" | "displays" | "approval" | "emergency" | "history" | "users";

const navigation: Array<{ id: AdminSection; label: string; icon: IconName; badge?: number }> = [
  { id: "dashboard", label: "ダッシュボード", icon: "grid" },
  { id: "media", label: "コンテンツ", icon: "film" },
  { id: "playlists", label: "プレイリスト", icon: "play" },
  { id: "schedule", label: "スケジュール", icon: "schedule" },
  { id: "displays", label: "ディスプレイ", icon: "display" },
  { id: "approval", label: "承認待ち", icon: "check", badge: 2 },
  { id: "emergency", label: "緊急配信", icon: "shield" },
];

const displayRows = [
  { name: "那覇メインLED", site: "那覇市・正面入口", status: "online", content: "サマーキャンペーン", next: "18:00", sync: "同期済み" },
  { name: "国際通りLED", site: "那覇市・国際通り", status: "online", content: "ブランドムービー", next: "20:00", sync: "同期済み" },
  { name: "浦添サブLED", site: "浦添市・東口", status: "online", content: "店舗案内ループ", next: "明日 09:00", sync: "同期済み" },
  { name: "北谷イベントLED", site: "北谷町・イベント広場", status: "warning", content: "週末イベント告知", next: "17:30", sync: "取得中 82%" },
  { name: "検証用ディスプレイ", site: "社内検証室", status: "offline", content: "テストパターン", next: "—", sync: "12分前に切断" },
];

function ResourceGate<T>({ resource, children }: { resource: Resource<T>; children: (value: T) => ReactNode }) {
  if (resource.state === "loading") return <p className="resource-note">読み込み中…</p>;
  if (resource.state === "error") {
    return <div className="inline-alert"><b>読み込めませんでした</b><span>{resource.message}{resource.code === null ? "" : `（${resource.code}）`}</span></div>;
  }
  return <>{children(resource.value)}</>;
}

function FixtureNotice() {
  if (!usingFixtures) return null;
  return <div className="resource-note resource-note--fixture">表示中のデータはローカル確認用のサンプルです。実APIを見るには <code>VITE_USE_FIXTURES=false pnpm run dev</code> で起動してください。</div>;
}

function StatusBadge({ status }: { status: string }) {
  const labels: Record<string, string> = { online: "稼働中", warning: "同期中", offline: "オフライン" };
  return <span className={`status-badge status-badge--${status}`}><span />{labels[status]}</span>;
}

function useDisplayViews() {
  const displays = useResource((signal) => api.listDisplays(signal), []);
  const media = useResource((signal) => api.listMedia(undefined, signal), []);
  return useMemo<Resource<ReturnType<typeof displayView>[]>>(() => {
    if (displays.state !== "ready") return displays as Resource<never>;
    const names = new Map(
      media.state === "ready" ? media.value.items.map((item) => [item.id, item.originalName] as const) : [],
    );
    return {
      state: "ready",
      value: displays.value.items.map((detail) =>
        displayView(detail, (id) => (id === null ? "—" : names.get(id) ?? id)),
      ),
    };
  }, [displays, media]);
}

function Dashboard() {
  const displays = useDisplayViews();
  return <>
    <div className="admin-heading"><div><p className="eyebrow eyebrow--blue">OVERVIEW</p><h1>おはようございます</h1><p>5台のLEDと本日の配信予定を確認できます。</p></div><div className="date-chip"><Icon name="schedule" />{new Date().toLocaleDateString("ja-JP", { year: "numeric", month: "long", day: "numeric", weekday: "long" })}</div></div>
    <FixtureNotice />
    <div className="metric-grid">
      <article><div><span>稼働中</span><strong>{displays.state === "ready" ? displays.value.filter(d => d.status !== "offline").length : "—"}<small>/ {displays.state === "ready" ? displays.value.length : "—"}台</small></strong></div><span className="metric-icon metric-icon--green"><Icon name="display" /></span><p>{displays.state === "ready" && displays.value.every(d => d.alerts.length === 0) ? <><b>● 正常</b> すべて予定どおり再生</> : <><b>● 要確認</b> 注意が必要なLEDがあります</>}</p></article>
      <article><div><span>本日の切替</span><strong>8<small>件</small></strong></div><span className="metric-icon metric-icon--blue"><Icon name="schedule" /></span><p>次回 <b>17:30</b> 北谷イベントLED</p></article>
      <article><div><span>承認待ち</span><strong>2<small>件</small></strong></div><span className="metric-icon metric-icon--coral"><Icon name="check" /></span><p><b>明日公開予定</b> の申請があります</p></article>
      <article><div><span>動画容量</span><strong>18.4<small>GB</small></strong></div><span className="metric-icon metric-icon--dark"><Icon name="film" /></span><p>全24本・今月 +3.2 GB</p></article>
    </div>
    <div className="admin-grid">
      <section className="panel panel--wide">
        <div className="panel__head"><div><h2>ディスプレイ状況</h2><p>最終通信と現在の再生状況</p></div><button type="button">すべて見る <Icon name="arrow" size={16} /></button></div>
        <ResourceGate resource={displays}>{(rows) => <div className="display-table"><div className="display-table__header"><span>ディスプレイ</span><span>状態</span><span>再生中</span><span>配信</span><span>注意</span></div>{rows.map(row => <div className="display-row" key={row.id}><span className="display-name"><span className="display-thumb"><Icon name="display" /></span><span><b>{row.name}</b><small>{row.id}</small></span></span><StatusBadge status={row.status}/><span>{row.playing}</span><span>{row.sync}</span><span>{row.alerts.length === 0 ? "—" : row.alerts.map(alert => <span className="danger-tag" key={alert}>! {alert}</span>)}</span></div>)}</div>}</ResourceGate>
      </section>
      <section className="panel timeline-panel"><div className="panel__head"><div><h2>今日の予定</h2><p>次の切り替え</p></div><button className="icon-button" type="button"><Icon name="arrow" /></button></div><div className="timeline"><div className="timeline__item timeline__item--done"><time>09:00</time><span /><div><b>店舗案内ループ</b><small>浦添サブLED</small></div></div><div className="timeline__item timeline__item--current"><time>17:30</time><span /><div><b>週末イベント告知</b><small>北谷イベントLED</small><em>次の予定</em></div></div><div className="timeline__item"><time>18:00</time><span /><div><b>キャンペーンB</b><small>那覇メインLED</small></div></div><div className="timeline__item"><time>20:00</time><span /><div><b>ナイトブランド動画</b><small>国際通りLED</small></div></div></div></section>
      <section className="panel approval-panel"><div className="panel__head"><div><h2>承認が必要です</h2><p>公開前の内容を確認してください</p></div><span className="count-badge">2</span></div><div className="approval-card"><div className="approval-preview preview--summer"><Icon name="play" /></div><div><b>夏休みセール 第2版</b><p>申請者：山田 太郎</p><small>公開予定：明日 09:00</small><div className="approval-actions"><button type="button">内容を確認</button><button type="button" className="approve">承認する</button></div></div></div></section>
      <section className="panel activity-panel"><div className="panel__head"><div><h2>最近の操作</h2><p>監査ログ</p></div><Icon name="history" /></div><ul className="activity-list"><li><span className="avatar">YK</span><p><b>山田 花子</b> が動画をアップロード<small>「brand_movie_2026.mp4」・12分前</small></p></li><li><span className="avatar avatar--blue">ST</span><p><b>佐藤 太郎</b> が公開を承認<small>「サマーキャンペーン」・38分前</small></p></li><li><span className="avatar avatar--green">SYS</span><p><b>システム</b> が配信版を適用<small>那覇メインLED・42分前</small></p></li></ul></section>
    </div>
  </>;
}

function SectionHeading({ eyebrow = "MANAGEMENT", title, description, action, icon, onAction }: { eyebrow?: string; title: string; description: string; action?: string; icon?: IconName; onAction?: () => void }) {
  return <div className="admin-heading"><div><p className="eyebrow eyebrow--blue">{eyebrow}</p><h1>{title}</h1><p>{description}</p></div>{action && icon && <button className="admin-primary" type="button" onClick={onAction}><Icon name={icon}/>{action}</button>}</div>;
}

function MediaSection() {
  const [reloadToken, setReloadToken] = useState(0);
  const [showUpload, setShowUpload] = useState(false);
  const media = useResource((signal) => api.listMedia(undefined, signal), [reloadToken]);
  return <>
    <SectionHeading title="コンテンツ" description="登録済み動画の検査状態、映像仕様、音声有無を確認します。" action="動画をアップロード" icon="upload" onAction={() => { setShowUpload(true); }} />
    <FixtureNotice />
    <ResourceGate resource={media}>{(assets) => <>
      <div className="filter-bar"><div className="search-box">⌕ <input aria-label="動画を検索" placeholder="動画名を検索" /></div><span>{assets.items.length}本・{byteLabel(assets.items.reduce((total, item) => total + item.byteSize, 0))}</span></div>
      <section className="panel content-panel"><div className="content-grid">{assets.items.map((item) => <article className="media-card" key={item.id}><div className="media-card__preview media-card__preview--blue"><span>LED</span><small>{durationLabel(item.durationMs)}</small></div><div className="media-card__body"><div><b>{item.originalName}</b></div><p>{durationLabel(item.durationMs)}・{resolutionLabel(item)}・{byteLabel(item.byteSize)}</p><div className="tag-row"><span className={item.hasAudio ? "risk-tag" : "neutral-tag"}>{item.hasAudio ? "音声あり" : "音声なし"}</span><span className={`media-state media-state--${mediaStatusTone(item.status)}`}>{mediaStatusLabel(item.status)}</span></div>{item.validationError !== null && <p className="media-card__error">{item.validationError}</p>}<MediaPreview mediaId={item.id} name={item.originalName}/>{item.status !== "archived" && <ArchiveMedia mediaId={item.id} name={item.originalName} onArchived={() => { setReloadToken((token) => token + 1); }}/>}</div></article>)}</div></section>
    </>}</ResourceGate>
    <div className="info-banner"><Icon name="shield"/><div><b>アップロード時に形式と完全性を検査します</b><p>MP4 / H.264 / AACを初期標準とし、非対応または破損した動画はプレイリストへ追加できません。</p></div></div>
    {showUpload && <MediaUploadDialog onClose={() => { setShowUpload(false); }} onUploaded={() => { setReloadToken((token) => token + 1); }} />}
  </>;
}

function DisplaysSection() {
  return <>
    <SectionHeading title="ディスプレイ" description="現地端末、通信状態、適用中の配信版を管理します。" icon="display" />
    <DisplayManagement />
    <DeviceEnrollment />
    <DeviceState />
    <div className="info-banner"><Icon name="shield"/><div><b>オフラインでも、端末内の確定済みスケジュールを再生します</b><p>「通信なし」は即座に黒画面を意味しません。最終通信、再生履歴、現地確認を組み合わせて判断してください。</p></div></div>
  </>;
}
function statusLabel(status: PublicationRequestStatus): string {
  const labels: Record<PublicationRequestStatus, string> = {
    draft: "下書き",
    submitted: "承認待ち",
    approved: "公開待ち",
    rejected: "差し戻し済み",
    cancelled: "取消済み",
    publishing: "公開処理中",
    published: "公開済み",
    failed: "公開に失敗",
  };
  return labels[status];
}

type ActionState =
  | { kind: "idle" }
  | {
      kind: "confirming";
      action: "approve" | "reject" | "publish";
      requestId: string;
    }
  | { kind: "running" }
  | { kind: "failed"; failure: ActionFailure };

/**
 * 承認前に見るべき情報を、承認する画面の中で出す。
 *
 * 対象LED、端末の接続状態、動画の準備状況、変更差分。別画面へ飛ばすと、
 * 見ないまま承認できてしまう。
 */
function PublicationDetail({ requestId }: { requestId: string }) {
  const detail = useResource(
    (signal) => api.getPublicationRequest(requestId, signal),
    [requestId],
  );

  if (detail.state === "loading") {
    return <p className="resource-note">詳細を読み込み中…</p>;
  }
  if (detail.state === "error") {
    return (
      <div className="inline-alert">
        <b>詳細を読み込めませんでした</b>
        <span>{detail.message}</span>
      </div>
    );
  }

  const preparation = preparationView(detail.value.preparation);
  const changes = detail.value.changes;

  return (
    <div className="publication-detail">
      <div className={`prep-summary ${preparation.ready ? "" : "prep-summary--pending"}`}>
        <b>{preparation.stateLabel}</b>
        <span>動画の準備: {preparation.assetProgress}</span>
      </div>

      <div className="approval-assets">
        <h4>公開される動画</h4>
        {detail.value.preparation.assets.length === 0 ? (
          <p className="resource-note">対象の動画がありません。</p>
        ) : (
          detail.value.preparation.assets.map((asset) => (
            <div className="approval-asset" key={asset.mediaAssetId}>
              <div className="approval-asset__head">
                <b>{asset.originalName}</b>
                {asset.audioEnabled ? (
                  <span className="risk-tag">音声あり</span>
                ) : (
                  <span className="neutral-tag">消音</span>
                )}
              </div>
              {asset.hasAudio && !asset.audioEnabled && (
                <p className="resource-note">
                  この動画は音声を持ちますが、プレイリストで消音にしています。
                </p>
              )}
              <MediaPreview
                mediaId={asset.mediaAssetId}
                name={asset.originalName}
              />
            </div>
          ))
        )}
      </div>

      <table className="prep-table">
        <thead>
          <tr><th>対象のLED</th><th>再生端末</th><th>状態</th><th>確認事項</th></tr>
        </thead>
        <tbody>
          {preparation.targets.map((target) => (
            <tr key={target.displayId}>
              <td>{target.displayId}</td>
              <td>{target.deviceId ?? "未登録"}</td>
              <td>
                <span className={`prep-state prep-state--${target.ready ? "ready" : "pending"}`}>
                  {target.connectivityLabel}
                </span>
              </td>
              <td>
                {target.issues.length === 0
                  ? "—"
                  : target.issues.map((issue) => <div key={issue}>{issue}</div>)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <h4>変更差分</h4>
      {changes.length === 0 ? (
        <p className="resource-note">差分の記録がありません。</p>
      ) : (
        <ul className="change-list">
          {changes.map((change) => (
            <li key={`${change.resourceType}-${change.resourceId}`}>
              <span className={`change-tag change-tag--${change.changeType}`}>
                {change.changeType === "added"
                  ? "追加"
                  : change.changeType === "removed"
                    ? "削除"
                    : "変更"}
              </span>
              <b>
                {change.resourceType === "schedule_revision"
                  ? "スケジュール"
                  : "対象LED"}
              </b>
              <code>{change.resourceId}</code>
              {change.before !== null && (
                <pre className="change-json">変更前: {JSON.stringify(change.before)}</pre>
              )}
              {change.after !== null && (
                <pre className="change-json">変更後: {JSON.stringify(change.after)}</pre>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ApprovalSection() {
  const [reloadToken, setReloadToken] = useState(0);
  const requests = useResource(
    (signal) => api.listPublicationRequests(signal),
    [reloadToken],
  );
  const [action, setAction] = useState<ActionState>({ kind: "idle" });
  const [reason, setReason] = useState("");
  // 再送しても二重適用されないよう、確認を開いた時点の鍵を使い回す
  const [idempotencyKey, setIdempotencyKey] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const begin = (
    kind: "approve" | "reject" | "publish",
    requestId: string,
  ) => {
    setReason("");
    setIdempotencyKey(newIdempotencyKey());
    setAction({ kind: "confirming", action: kind, requestId });
  };

  const run = async (
    kind: "approve" | "reject" | "publish",
    requestId: string,
  ) => {
    setAction({ kind: "running" });
    try {
      if (kind === "approve") {
        await api.approvePublicationRequest(requestId, {}, idempotencyKey);
      } else if (kind === "publish") {
        await api.publishPublicationRequest(requestId, idempotencyKey);
      } else {
        await api.rejectPublicationRequest(requestId, { reason }, idempotencyKey);
      }
      setAction({ kind: "idle" });
      setReloadToken((token) => token + 1);
    } catch (error: unknown) {
      setAction({ kind: "failed", failure: describeFailure(error) });
    }
  };

  return <>
    <SectionHeading eyebrow="REVIEW & APPROVAL" title="承認待ち" description="申請者とは別の担当者が、差分とリスクを確認して公開します。" />
    <PermissionNotice permission="publication:review" action="承認・差し戻し" />
    <PermissionNotice permission="publication:publish" action="公開" />
    <FixtureNotice />
    {action.kind === "running" && <p className="resource-note">送信中…</p>}
    {action.kind === "failed" && <div className="inline-alert"><b>操作できませんでした</b><span>{action.failure.message}{action.failure.code === null ? "" : `（${action.failure.code}）`}</span><AuthenticationRecoveryButton failure={action.failure}/>{action.failure.reloadHelps && <button className="secondary-button" type="button" onClick={() => { setAction({ kind: "idle" }); setReloadToken((token) => token + 1); }}>最新の内容を読み込む</button>}{!action.failure.reloadHelps && !action.failure.fullPageReloadRequired && <button className="secondary-button" type="button" onClick={() => { setAction({ kind: "idle" }); }}>閉じる</button>}</div>}
    <ResourceGate resource={requests}>{(page) => page.items.length === 0
      ? <p className="resource-note">承認待ちの申請はありません。</p>
      : <div className="approval-grid">{page.items.filter((request) => request.status === "submitted" || request.status === "approved").map((request) => { const view = approvalView(request); return <article className="panel review-card" key={view.id}><div className="review-preview review-preview--blue"><span>PREVIEW</span><Icon name="play" size={30}/></div><div className="review-content"><div className="review-title"><div><span className="request-number">申請 {view.id}</span><h2>{view.title}</h2></div><span className="pending-label">{statusLabel(request.status)}</span></div><dl><div><dt>申請者</dt><dd>{request.requestedBy}</dd></div><div><dt>申請日時</dt><dd>{request.submittedAt ?? "—"}</dd></div><div><dt>対象</dt><dd>{view.displayCount}台</dd></div></dl>{view.warnings.length > 0 && <ul className="risk-list">{view.warnings.map(warning => <li className={`risk-item risk-item--${warning.severity}`} key={warning.code}><b>{warning.label}</b><span>{warning.detail}</span></li>)}</ul>}{view.blocking.length > 0 && <div className="inline-alert"><b>このままでは公開できません</b><span>{view.blocking.join(" / ")}</span></div>}{view.blocking.length === 0 && view.advisory.length > 0 && <div className="inline-alert"><b>確認が必要です</b><span>{view.advisory.join(" / ")}</span></div>}<div className="review-actions">{request.status !== "submitted" && request.status !== "approved" ? <span className="resource-note">この申請は{statusLabel(request.status)}です。操作はありません。</span> : request.status === "approved" ? <><span className="resource-note">承認済みです。LEDへ届けるには公開が必要です。</span><button className="admin-primary" type="button" disabled={action.kind === "running"} onClick={() => { begin("publish", view.id); }}><Icon name="play"/>公開する</button></> : <><button className="secondary-button" type="button" disabled={action.kind === "running"} onClick={() => { begin("reject", view.id); }}>差し戻す</button><button className="secondary-button" type="button" onClick={() => { setExpandedId(expandedId === view.id ? null : view.id); }}>{expandedId === view.id ? "詳細を閉じる" : "内容と差分を見る"}</button><button className="admin-primary" type="button" disabled={view.blocking.length > 0 || action.kind === "running"} onClick={() => { begin("approve", view.id); }}><Icon name="check"/>内容を確認して承認</button></>}</div>{expandedId === view.id && <PublicationDetail requestId={view.id} />}{action.kind === "confirming" && action.requestId === view.id && <div className="confirm-panel">{action.action === "publish" ? <><b>この内容を公開します</b><p>対象{view.displayCount}台のLEDへ配信版が生成され、端末が次回の同期で取得します。公開後の取り消しはロールバック操作になります。</p></> : action.action === "approve" ? <><b>この内容を承認します</b><p>承認しても、この時点ではLEDへ何も届きません。承認は内容を確定するだけで、端末への配信は管理者が「公開する」を実行したときに始まります。{view.warnings.length > 0 && <> 次の点を確認してください: {view.warnings.map(warning => warning.label).join(" / ")}。</>}</p></> : <><b>差し戻します</b><p>申請者へ返す理由を入力してください。理由は監査ログに記録されます。</p><textarea rows={3} value={reason} onChange={(changed) => { setReason(changed.target.value); }} placeholder="差し戻す理由" /></>}<div className="confirm-actions"><button className="secondary-button" type="button" onClick={() => { setAction({ kind: "idle" }); }}>やめる</button><button className={action.action === "reject" ? "danger-button" : "admin-primary"} type="button" disabled={action.action === "reject" && reason.trim().length === 0} onClick={() => { void run(action.action, view.id); }}>{action.action === "approve" ? "承認を確定する" : action.action === "publish" ? "公開を確定する" : "差し戻しを確定する"}</button></div></div>}</div></article>; })}</div>}</ResourceGate>
    <div className="separation-note"><Icon name="users"/><div><b>作成者と承認者を分離しています</b><p>自分が申請した公開内容は、自分では承認できません。承認・差し戻し・理由は監査ログに記録されます。</p></div></div>
  </>;
}

function AuditHistory() {
  const [search, setSearch] = useState("");
  const [outcome, setOutcome] = useState<"all" | "success" | "failure">("all");
  const logs = useResource(
    (signal) => api.listAuditLogs(
      { limit: 50, ...(outcome === "all" ? {} : { outcome }) },
      signal,
    ),
    [outcome],
  );
  return <>
    <div className="filter-bar">
      <div className="search-box">⌕ <input aria-label="履歴を検索" placeholder="実行者・操作・対象を検索" value={search} onChange={(event) => { setSearch(event.target.value); }}/></div>
      <label className="audit-filter">結果<select aria-label="結果で絞り込む" value={outcome} onChange={(event) => { setOutcome(event.target.value as typeof outcome); }}><option value="all">すべて</option><option value="success">成功</option><option value="failure">失敗</option></select></label>
      <span>最新50件（検索もこの範囲）</span>
    </div>
    <ResourceGate resource={logs}>{(page) => {
      const needle = search.trim().toLocaleLowerCase("ja-JP");
      const visible = page.items.map(auditLogView).filter((log) =>
        needle.length === 0 || [log.actor, log.action, log.target, log.detail ?? ""].some((value) => value.toLocaleLowerCase("ja-JP").includes(needle)),
      );
      if (page.items.length === 0) return <p className="resource-note">操作履歴はまだありません。</p>;
      if (visible.length === 0) return <p className="resource-note">条件に一致する操作履歴はありません。</p>;
      return <section className="panel audit-table"><div className="audit-row audit-row--head"><span>日時（JST）</span><span>実行者</span><span>操作</span><span>対象</span><span>結果</span></div>{visible.map((log) => <div className="audit-row" key={log.id}><time>{log.date}<br/>{log.time}</time><span>{log.actor}</span><b>{log.action}</b><span>{log.target}{log.detail !== null && <small className="audit-detail">{log.detail}</small>}</span><span className={log.outcome === "success" ? "audit-ok" : "audit-warn"}>{log.outcomeLabel}</span></div>)}</section>;
    }}</ResourceGate>
  </>;
}

function HistorySection() {
  const [kind, setKind] = useState<"audit" | "playback">("audit");
  return <>
    <SectionHeading
      title="履歴"
      description="管理画面の操作と、LEDで実際に起きた再生を分けて追跡します。"
    />
    <FixtureNotice />
    <div className="history-tabs" role="tablist" aria-label="履歴の種類">
      <button
        type="button"
        role="tab"
        aria-selected={kind === "audit"}
        className={kind === "audit" ? "active" : ""}
        onClick={() => { setKind("audit"); }}
      >
        操作履歴
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={kind === "playback"}
        className={kind === "playback" ? "active" : ""}
        onClick={() => { setKind("playback"); }}
      >
        再生実績
      </button>
    </div>
    {kind === "audit" ? <AuditHistory /> : <PlaybackHistory />}
  </>;
}

/**
 * メンバー管理。
 *
 * **管理APIにメンバー管理のendpointが無い**ため、まだ画面から操作できない。
 * 架空の利用者を並べると、実在しない人に権限があるように見える。実装が
 * 無いことをそのまま出す。
 */
function UsersSection() {
  const session = useSession();
  return (
    <>
      <SectionHeading
        title="メンバー管理"
        description="役割を分け、必要最小限の操作権限を設定します。"
        icon="users"
      />

      <div className="inline-alert">
        <b>メンバー管理はまだ画面から行えません</b>
        <span>
          追加・役割変更・削除に対応する管理APIが未実装です。現在は
          データベースへ直接登録しています。
        </span>
        <span>
          運用を始める前に、この画面から操作できるようにする必要があります。
        </span>
      </div>

      {session !== null && session.state === "ready" && (
        <section className="panel member-panel">
          <div className="member-row">
            <span className="avatar">
              {session.value.displayName.slice(0, 2)}
            </span>
            <span>
              <b>{session.value.displayName}</b>
              <small>{session.value.email}</small>
            </span>
            <span className="role-badge">
              {session.value.roles.join(" / ")}
            </span>
            <span>{session.value.organizationName}</span>
          </div>
        </section>
      )}
      <p className="resource-note">
        表示できるのは、いまサインインしている利用者だけです（
        <code>GET /api/v1/me</code>）。組織の全メンバーを一覧する手段がまだ
        ありません。
      </p>

      <div className="separation-note">
        <Icon name="shield" />
        <div>
          <b>権限はアプリケーションDBで管理します</b>
          <p>
            Cloudflare Accessで本人を確認し、閲覧者・編集者・承認者・管理者の
            役割に応じて操作を制限します。
          </p>
        </div>
      </div>
    </>
  );
}

function ManagementSection({ section }: { section: Exclude<AdminSection, "dashboard"> }) {
  const sections: Record<Exclude<AdminSection,"dashboard">, ReactNode> = { media:<MediaSection/>, playlists:<PlaylistSection/>, schedule:<ScheduleSection/>, displays:<DisplaysSection/>, approval:<ApprovalSection/>, emergency:<EmergencySection/>, history:<HistorySection/>, users:<UsersSection/> };
  return sections[section];
}

function AdminConsole() {
  return (
    <SessionProvider>
      <AdminConsoleBody />
    </SessionProvider>
  );
}

function AdminConsoleBody() {
  const [section, setSection] = useState<AdminSection>("dashboard");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const title = useMemo(() => {
    if (section === "history") return "操作履歴";
    if (section === "users") return "メンバー管理";
    return navigation.find((item) => item.id === section)?.label ?? "ダッシュボード";
  }, [section]);
  return (
    <div className="admin-shell">
      <button className={`sidebar-overlay ${mobileNavOpen ? "sidebar-overlay--open" : ""}`} type="button" aria-label="メニューを閉じる" onClick={() => setMobileNavOpen(false)} />
      <aside className={`sidebar ${mobileNavOpen ? "sidebar--open" : ""}`}>
        <button className="sidebar-close" type="button" aria-label="メニューを閉じる" onClick={() => setMobileNavOpen(false)}>×</button>
        <Brand inverse />
        <div className="workspace"><span>WORKSPACE</span><button type="button"><span className="workspace__mark">G</span><span><b>GO-TAs Signage</b><small>本番環境</small></span><span>⌄</span></button></div>
        <nav aria-label="管理画面メニュー">{navigation.map(item => <button type="button" key={item.id} className={section === item.id ? "active" : ""} onClick={() => { setSection(item.id); setMobileNavOpen(false); }}><Icon name={item.icon}/><span>{item.label}</span>{item.badge && <em>{item.badge}</em>}</button>)}</nav>
        <div className="sidebar__bottom"><button type="button" className={section === "history" ? "active" : ""} onClick={() => { setSection("history"); setMobileNavOpen(false); }}><Icon name="history"/><span>操作履歴</span></button><button type="button" className={section === "users" ? "active" : ""} onClick={() => { setSection("users"); setMobileNavOpen(false); }}><Icon name="users"/><span>メンバー管理</span></button><a href="/">公開サイトへ <Icon name="arrow" size={16}/></a><div className="account"><CurrentUserBadge /><span><b>Yu Aoi</b><small>管理者</small></span><span>•••</span></div></div>
      </aside>
      <div className="admin-main">
        <header className="admin-topbar"><button className="mobile-menu" type="button" aria-label="メニューを開く" onClick={() => setMobileNavOpen(true)}><Icon name="menu"/></button><div><span>LED SIGNAGE</span><b>{title}</b></div><div className="topbar-actions"><span className="system-health"><i />システム正常</span><button type="button" aria-label="通知">●</button><span className="avatar">YA</span></div></header>
        <main className="admin-content">{section === "dashboard" ? <Dashboard /> : <ManagementSection section={section} />}</main>
      </div>
    </div>
  );
}

export function App() {
  const isConsole = window.location.pathname.startsWith("/console");
  return isConsole ? <AdminConsole /> : <LandingPage />;
}
